import React, { useState } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Typography, Grid, IconButton, TextField, Divider, Chip } from '@mui/material';
import { Trash2, Plus, Minus, ShoppingCart, Home, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([
    { id: 1, title: 'Premium Wireless Headphones', price: 299, quantity: 1, image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=100&h=100&fit=crop', size: 'M', color: 'Black' },
    { id: 2, title: 'Smart Watch', price: 250, quantity: 2, image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100&h=100&fit=crop', size: 'L', color: 'White' },
  ]);
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState(0);

  const suggestedProducts = [
    { id: 3, title: 'Bluetooth Speaker', price: 89, image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=200&h=200&fit=crop' },
    { id: 4, title: 'Phone Case', price: 29, image: 'https://images.unsplash.com/photo-1565849904461-04a58ad377e0?w=200&h=200&fit=crop' },
    { id: 5, title: 'Laptop Stand', price: 45, image: 'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=200&h=200&fit=crop' },
  ];

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(items =>
      items.map(item =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const removeItem = (id) => {
    setCartItems(items => items.filter(item => item.id !== id));
  };

  const applyCoupon = () => {
    if (coupon.toLowerCase() === 'save10') {
      setDiscount(10);
    } else {
      setDiscount(0);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountAmount = (subtotal * discount) / 100;
  const total = subtotal - discountAmount;

  return (
    <Box sx={{ mt: 7, mx: 2, maxWidth: '1000px', mx: 'auto' }}>
      {/* Simple Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Button startIcon={<Home />} onClick={() => navigate('/Ecommerce')}>Home</Button>
        <Typography variant="h5">Shopping Cart</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => navigate('/CartPage')}><ShoppingCart /></IconButton>
          <IconButton onClick={() => navigate('/UserDashboard')}><User /></IconButton>
        </Box>
      </Box>

      {cartItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingCart size={64} />
          <Typography variant="h6" sx={{ mt: 2 }}>Your cart is empty</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/ShopPage')}>
            Continue Shopping
          </Button>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {/* Cart Items */}
          <Grid item xs={12} md={8}>
            {cartItems.map((item) => (
              <Card key={item.id} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <CardMedia
                    component="img"
                    image={item.image}
                    alt={item.title}
                    sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Size: {item.size} | Color: {item.color}
                    </Typography>
                    <Typography variant="h6" color="primary">${item.price}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                        <Minus size={16} />
                      </IconButton>
                      <Typography>{item.quantity}</Typography>
                      <IconButton size="small" onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                        <Plus size={16} />
                      </IconButton>
                    </Box>
                    <IconButton color="error" onClick={() => removeItem(item.id)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Order Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal:</Typography>
                <Typography>${subtotal.toFixed(2)}</Typography>
              </Box>
              {discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'success.main' }}>
                  <Typography>Discount ({discount}%):</Typography>
                  <Typography>-${discountAmount.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">${total.toFixed(2)}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter coupon code"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button variant="outlined" fullWidth onClick={applyCoupon}>
                  Apply Coupon
                </Button>
              </Box>

              <Button variant="contained" fullWidth size="large" onClick={() => navigate('/CheckoutPage')}>
                Proceed to Checkout
              </Button>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* You May Also Like */}
      {cartItems.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>You May Also Like</Typography>
          <Grid container spacing={2}>
            {suggestedProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card
                  component={motion.div}
                  whileHover={{ y: -5 }}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate('/ProductPage')}
                >
                  <CardMedia component="img" height="200" image={product.image} alt={product.title} />
                  <CardContent>
                    <Typography variant="h6">{product.title}</Typography>
                    <Typography variant="h6" color="primary">${product.price}</Typography>
                    <Button variant="outlined" fullWidth sx={{ mt: 1 }}>Add to Cart</Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default CartPage;